import { db, fieldPath } from "~/setups/firebase";
import { AppointmentStatus, envVars } from "@ambii-com/core";
import type { EnvVars, SyncSignalCollectionName } from "@ambii-com/core";
import {
  ClinicLifeCycleState,
  SubmissionLifeCycleState,
  FormQuestionComponents,
} from "@ambii-com/core";
import {
  CollectionReference,
  DocumentData,
  DocumentReference,
  FirebaseFirestore,
  Query,
  Timestamp,
  DocumentSnapshot,
} from "@firebase/firestore-types";

function requireId(id: any, name: string): string {
  if (typeof id !== "string" || id.trim() === "") {
    throw new Error(`[FsLocations] ${name} is required (got: ${JSON.stringify(id)})`)
  }
  return id
}

function makeSyncSignalDocId(
  clinicId: ClinicId,
  collectionName: SyncSignalCollectionName,
  targetDocId?: string
): string {
  return targetDocId
    ? `${clinicId}__${collectionName}__${targetDocId}`
    : `${clinicId}__${collectionName}`;
}

class FsLocations {
  formsRef: CollectionReference<DocumentData>;
  clinicsRef: CollectionReference<DocumentData>;
  // usersRef: CollectionReference<DocumentData>
  patientsRef: CollectionReference<DocumentData>;
  adminsRef: CollectionReference<DocumentData>;
  appointmentMenusRef: CollectionReference<DocumentData>;
  appointmentSchedulesRef: CollectionReference<DocumentData>;
  appointmentsRef: CollectionReference<DocumentData>;
  pdfExportFormsRef: CollectionReference<DocumentData>;
  formConfigsRef: CollectionReference<DocumentData>;
  vaccineConfigsRef: CollectionReference<DocumentData>;
  formSubmissionsRef: CollectionReference<DocumentData>;
  formSectionsRef: CollectionReference<DocumentData>;
  formPagesRef: CollectionReference<DocumentData>;
  formQuestionsRef: CollectionReference<DocumentData>;
  appointmentConfigsRef: CollectionReference<DocumentData>;
  postsRef: CollectionReference<DocumentData>;
  scoringGroupsRef: CollectionReference<DocumentData>;
  vaccineMenuSettingsRef: CollectionReference<DocumentData>;
  vaccineAppointmentDetailsRef: CollectionReference<DocumentData>;
  syncSignalsRef: CollectionReference<DocumentData>;
  tvConfigsRef: CollectionReference<DocumentData>;
  queueConfigsRef: CollectionReference<DocumentData>;
  queueEntriesRef: CollectionReference<DocumentData>;
  cancelationPoliciesRef: CollectionReference<DocumentData>;
  db: FirebaseFirestore;
  envVars: EnvVars;

  constructor(_db: FirebaseFirestore = db, _envVars: EnvVars = envVars) {
    this.db = _db;
    this.envVars = _envVars;
    this.clinicsRef = this.db.collection(this.envVars.clinicCollectionName);
    // this.usersRef = this.db.collection(this.envVars.userCollectionName)
    this.patientsRef = this.db.collection(this.envVars.patientCollectionName);
    this.adminsRef = this.db.collection(this.envVars.adminCollectionName);
    this.appointmentMenusRef = this.db.collection(
      this.envVars.appointmentMenusCollectionName
    );
    this.appointmentSchedulesRef = this.db.collection(
      this.envVars.appointmentSchedulesCollectionName
    );
    this.appointmentsRef = this.db.collection(
      this.envVars.scheduleAppointmentCollectionName
    );
    this.formConfigsRef = this.db.collection(
      this.envVars.formsConfigCollectionName
    );
    this.appointmentConfigsRef = this.db.collection(
      this.envVars.appointmentConfigsCollectionName
    );
    this.vaccineConfigsRef = this.db.collection(
      this.envVars.vaccineConfigCollectionName
    );
    this.formSubmissionsRef = this.db.collection(
      this.envVars.submissionsCollectionName
    );
    this.formsRef = this.db.collection(this.envVars.formsCollectionName);
    this.pdfExportFormsRef = this.db.collection("pdfExportForms");
    this.formPagesRef = this.db.collection(
      this.envVars.formPagesCollectionName
    );
    this.formQuestionsRef = this.db.collection(
      this.envVars.questionsCollectionName
    );
    this.postsRef = this.db.collection(this.envVars.postsCollectionName);
    this.scoringGroupsRef = this.db.collection(
      this.envVars.scoringGroupsCollectionName
    );
    this.vaccineMenuSettingsRef = this.db.collection(this.envVars.vaccineMenuSettingsCollectionName);
    this.vaccineAppointmentDetailsRef = this.db.collection(this.envVars.vaccineAppointmentDetailsCollectionName);
    this.syncSignalsRef = this.db.collection(
      this.envVars.syncSignalsCollectionName
    );
    this.tvConfigsRef = this.db.collection(this.envVars.tvConfigsCollectionName);
    this.queueConfigsRef = this.db.collection(this.envVars.queueConfigsCollectionName);
    this.queueEntriesRef = this.db.collection(this.envVars.queueEntriesCollectionName);
    this.cancelationPoliciesRef = this.db.collection(
      this.envVars.cancelationPoliciesCollectionName
    );
  }
  getCancelationPolicyRef(policyId: string): DocumentReference<DocumentData> {
    return this.cancelationPoliciesRef.doc(requireId(policyId, "policyId"));
  }
  getCancelationPoliciesRefByClinicId(clinicId: ClinicId): Query<DocumentData> {
    return this.cancelationPoliciesRef.where("clinicId", "==", clinicId);
  }
  getClinicRef(clinicId: ClinicId): DocumentReference<DocumentData> {
    return this.clinicsRef.doc(requireId(clinicId, "clinicId"));
  }
  getClinicsRef(): CollectionReference<DocumentData> {
    return this.clinicsRef;
  }
  getClinicRefByUserId(userId: UserId): Query<DocumentData> {
    return this.clinicsRef.where("admin.uuid", "==", userId);
  }
  getMenuRef(menuId: MenuId): DocumentReference<DocumentData> {
    return this.appointmentMenusRef.doc(requireId(menuId, "menuId"));
  }
  getMenusRefByClinicId(clinicId: ClinicId): Query<DocumentData> {
    return this.appointmentMenusRef.where("clinicId", "==", clinicId);
  }
  getMenusForPatientRefByClinicId(clinicId: ClinicId): Query<DocumentData> {
    return this.getMenusRefByClinicId(clinicId).where(
      "shownForPatient",
      "==",
      true
    );
  }
  // getScheduleResourcesRefByMenuId(menuId: MenuId): Query<DocumentData> {
  //   return this.scheduleResourcesRef.where("menuId", "==", menuId);
  // }
  // getScheduleResourcesForPatientRefByMenuId(
  //   menuId: MenuId
  // ): Query<DocumentData> {
  //   return this.getScheduleResourcesRefByMenuId(menuId).where(
  //     "shownForPatient",
  //     "==",
  //     true
  //   );
  // }
  // getScheduleResourcesRefByClinicId(clinicId: ClinicId): Query<DocumentData> {
  //   return this.scheduleResourcesRef.where("clinicId", "==", clinicId);
  // }
  getScheduleRef(scheduleId: ScheduleId): DocumentReference<DocumentData> {
    return this.appointmentSchedulesRef.doc(requireId(scheduleId, "scheduleId"));
  }
  getSchedulesRefByMenuIdLegacy(menuId: MenuId): Query<DocumentData> {
    return this.appointmentSchedulesRef.where("resourceId", "==", menuId);
  }
  getSchedulesRefByMenuId(menuId: MenuId): Query<DocumentData> {
    return this.appointmentSchedulesRef.where(
      "resourceId",
      "array-contains",
      menuId
    );
  }
  getScheduleBucketsRefByClinicIdAndDateRange(
    clinicId: ClinicId,
    startDate: string,
    endDate: string
  ): Query<DocumentData> {
    return this.appointmentSchedulesRef
      .where("clinicId", "==", clinicId)
      .where("date", ">=", startDate)
      .where("date", "<=", endDate);
  }
  getAppointmentScheduleRef(
    scheduleId: ScheduleId
  ): DocumentReference<DocumentData> {
    return this.appointmentSchedulesRef.doc(requireId(scheduleId, "scheduleId"));
  }
  getScheduleAppointmentsRefByClinicIdAndMenuId(
    clinicId: ClinicId,
    menuId: MenuId
  ): Query<DocumentData> {
    return this.appointmentsRef
      .where("menuId", "==", menuId)
      .where("clinicId", "==", clinicId);
  }
  getScheduleAppointmentsRefByBucketId(
    bucketId: BucketId
  ): Query<DocumentData> {
    return this.appointmentsRef.where("bucketId", "==", bucketId);
  }
  // Note: in order for the fs rules for admins to work, querys must also contain clinicId
  getScheduleAppointmentsRefByClinicIdAndBucketId(
    clinicId: ClinicId,
    bucketId: BucketId
  ): Query<DocumentData> {
    return this.appointmentsRef
      .where("bucketId", "==", bucketId)
      .where("clinicId", "==", clinicId);
  }
  getScheduleAppointmentsRefByClinicIdAndBucketIdAndDate(
    clinicId: ClinicId,
    bucketId: BucketId,
    date: string
  ): Query<DocumentData> {
    return this.getScheduleAppointmentsRefByClinicIdAndBucketId(
      clinicId,
      bucketId
    ).where("date", "==", date);
  }
  getScheduleAppointmentsRefByBucketIdAndUserId(
    bucketId: BucketId,
    userId: UserId
  ): Query<DocumentData> {
    return this.appointmentsRef
      .where("bucketId", "==", bucketId)
      .where("userId", "==", userId);
  }
  getScheduleAppointmentsRefByUserId(userId: UserId): Query<DocumentData> {
    return this.appointmentsRef.where("userId", "==", userId);
  }
  // getScheduleAppointmentsRefByMenuIdAndUserId(menuId: MenuId, userId: UserId): Query<DocumentData> {
  //   return this.appointmentsRef.where("menuId", "==", menuId).where("userId", "==", userId)
  // }
  getScheduleAppointmentsRefByResourceIdAndUserId(
    resourceId: ResourceId,
    userId: UserId
  ): Query<DocumentData> {
    return this.appointmentsRef
      .where("resourceId", "==", resourceId)
      .where("userId", "==", userId);
  }
  getScheduleAppointmentsRefByClinicId(
    clinicId: ClinicId
  ): Query<DocumentData> {
    return this.appointmentsRef.where("clinicId", "==", clinicId);
  }
  getScheduleAppointmentsRefByClinicIdAndStatusIsCanceledAndDateRange(
    clinicId: ClinicId,
    startDate: string,
    endDate: string
  ): Query<DocumentData> {
    return this.appointmentsRef
      .where("clinicId", "==", clinicId)
      .where("status", "==", AppointmentStatus.CANCELED)
      .where("date", ">=", startDate)
      .where("date", "<=", endDate);
  }
  getScheduleAppointmentsRefByClinicIdAndClinicLifeCycleState(
    clinicId: ClinicId,
    clinicLifeCycleState: ClinicLifeCycleState
  ): Query<DocumentData> {
    return this.getScheduleAppointmentsRefByClinicId(clinicId).where(
      "clinicLifeCycleState",
      "==",
      clinicLifeCycleState
    );
  }
  getScheduleAppointmentsRefByClinicIdAndDateRange(
    clinicId: ClinicId,
    startDate: string,
    endDate: string
  ): Query<DocumentData> {
    return this.appointmentsRef
      .where("clinicId", "==", clinicId)
      .where("date", ">=", startDate)
      .where("date", "<=", endDate);
  }
  getScheduleAppointmentsRefByClinicIdAndMenuIdAndClinicLifeCycleState(
    clinicId: ClinicId,
    menuId: string,
    clinicLifeCycleState: ClinicLifeCycleState
  ): Query<DocumentData> {
    return this.getScheduleAppointmentsRefByClinicIdAndMenuId(
      clinicId,
      menuId
    ).where("clinicLifeCycleState", "==", clinicLifeCycleState);
  }
  getScheduleAppointmentRef(
    appointmentId: AppointmentId
  ): DocumentReference<DocumentData> {
    return this.appointmentsRef.doc(requireId(appointmentId, "appointmentId"));
  }
  getPatientsRef(userId: UserId): DocumentReference<DocumentData> {
    return this.patientsRef.doc(requireId(userId, "userId"));
  }
  getAdminsRef(userId: UserId): DocumentReference<DocumentData> {
    return this.adminsRef.doc(requireId(userId, "userId"));
  }
  getPatientsRefByUserId(userId: UserId) {
    return this.patientsRef.where("uid", "==", userId).limit(1);
  }
  getAdminsRefByUserId(userId: UserId) {
    return this.adminsRef.where("uid", "==", userId).limit(1);
  }
  getFormConfigRef(configId: string): DocumentReference<DocumentData> {
    return this.formConfigsRef.doc(requireId(configId, "configId"));
  }
  getFormConfigRefByShortLinkPath(shortLinkPath: string): Query<DocumentData> {
    return this.formConfigsRef
      .where("shortLinkPath", "==", shortLinkPath)
      .limit(1);
  }
  // TODO Add clinicId to all querys when access controls based on clinicId are added
  getFormsRefByClinicId(clinicId: ClinicId): CollectionReference<DocumentData> {
    return this.getClinicRef(clinicId).collection(
      this.envVars.formsCollectionName
    );
  }
  getFormRefsByClinicId(clinicId: ClinicId): Query<DocumentData> {
    return this.formsRef.where("clinicId", "==", clinicId);
  }
  getFormTemplateRefs(): Query<DocumentData> {
    return this.formsRef.where("isTemplate", "==", true);
  }
  getFormSubmissionRef(id: string) {
    return this.formSubmissionsRef.doc(requireId(id, "id"));
  }
  getFormSubmissionRefByAppointmentId(
    clinicId: ClinicId,
    appointmentId: AppointmentId
  ): Query<DocumentData> {
    return this.formSubmissionsRef
      .where("clinicId", "==", requireId(clinicId, "clinicId"))
      .where("appointmentId", "==", requireId(appointmentId, "appointmentId"));
  }
  getFormRef(formId: FormId) {
    return this.formsRef.doc(requireId(formId, "formId"));
  }
  getFormRefByAppointmentId(
    clinicId: ClinicId,
    appointmentId: string
  ): Query<DocumentData> {
    return this.getFormsRefByClinicId(clinicId)
      .where("appointment.appointmentId", "==", appointmentId)
      .limit(1);
  }
  getFormSubmissionRefByClinicId(clinicId: ClinicId) {
    return this.formSubmissionsRef.where("clinicId", "==", clinicId);
  }
  getFormSubmissionsByClinicIdAndState(
    clinicId: ClinicId,
    lifeCycleStates: SubmissionLifeCycleState[],
    currentDoc: DocumentSnapshot,
    direction: "prev" | "next"
  ) {
    const baseQuery = this.getFormSubmissionRefByClinicId(clinicId)
      .where("lifeCycleState", "in", lifeCycleStates)
      .orderBy("createdAt", "desc");

    if (direction === "prev") {
      return baseQuery.endBefore(currentDoc).limitToLast(1).get();
    } else {
      return baseQuery.startAfter(currentDoc).limit(1).get();
    }
  }
  getPrevFormSubmissionByClinicId(
    clinicId: ClinicId,
    currentDoc: DocumentSnapshot
  ) {
    return this.getFormSubmissionsByClinicIdAndState(
      clinicId,
      [SubmissionLifeCycleState.CREATED, SubmissionLifeCycleState.UNARCHIVED],
      currentDoc,
      "prev"
    );
  }
  getNextFormSubmissionByClinicId(
    clinicId: ClinicId,
    currentDoc: DocumentSnapshot
  ) {
    return this.getFormSubmissionsByClinicIdAndState(
      clinicId,
      [SubmissionLifeCycleState.CREATED, SubmissionLifeCycleState.UNARCHIVED],
      currentDoc,
      "next"
    );
  }
  getPrevArchivedFormSubmissionByClinicId(
    clinicId: ClinicId,
    currentDoc: DocumentSnapshot
  ) {
    return this.getFormSubmissionsByClinicIdAndState(
      clinicId,
      [SubmissionLifeCycleState.ARCHIVED],
      currentDoc,
      "prev"
    );
  }
  getNextArchivedFormSubmissionByClinicId(
    clinicId: ClinicId,
    currentDoc: DocumentSnapshot
  ) {
    return this.getFormSubmissionsByClinicIdAndState(
      clinicId,
      [SubmissionLifeCycleState.ARCHIVED],
      currentDoc,
      "next"
    );
  }
  getFormSubmissionRefByClinicIdAndDateRange(
    clinicId: ClinicId,
    startDate: Timestamp,
    endDate: Timestamp
  ) {
    return this.formSubmissionsRef
      .where("clinicId", "==", clinicId)
      .where("createdAt", ">=", startDate)
      .where("createdAt", "<=", endDate);
  }
  getCreatedFormSubmissionRefByClinicId(clinicId: ClinicId) {
    return this.formSubmissionsRef
      .where("clinicId", "==", clinicId)
      .where("lifeCycleState", "==", SubmissionLifeCycleState.CREATED);
  }
  getActiveFormSubmissionRefByClinicId(clinicId: ClinicId) {
    return this.formSubmissionsRef
      .where("clinicId", "==", clinicId)
      .where("lifeCycleState", "in", [
        SubmissionLifeCycleState.CREATED,
        SubmissionLifeCycleState.UNARCHIVED,
      ]);
  }
  getArchivedFormSubmissionRefByClinicId(clinicId: ClinicId) {
    return this.formSubmissionsRef
      .where("clinicId", "==", clinicId)
      .where("lifeCycleState", "==", SubmissionLifeCycleState.ARCHIVED);
  }
  getFormSectionRef(id: string) {
    return this.formSectionsRef.doc(requireId(id, "id"));
  }
  getFormPageRef(id: string) {
    return this.formPagesRef.doc(requireId(id, "id"));
  }
  getFormPageRefsByClinicId(clinicId: ClinicId): Query<DocumentData> {
    return this.formPagesRef.where("clinicId", "==", clinicId);
  }
  getFormPageRefsByFormId(formId: FormId): Query<DocumentData> {
    return this.formPagesRef.where("formId", "==", formId);
  }
  getFormQuestionRef(questionId: QuestionId): DocumentReference<DocumentData> {
    return this.formQuestionsRef.doc(requireId(questionId, "questionId"));
  }
  getFormQuestionRefByPageId(pageId: PageId): Query<DocumentData> {
    return this.formQuestionsRef.where("pageId", "==", pageId);
  }
  getFormQuestionRefByFormId(formId: FormId): Query<DocumentData> {
    return this.formQuestionsRef.where("formId", "==", formId);
  }
  // requires firestore index in prd
  getPatientInfoFormQuestionRefsByFormId(formId: FormId) {
    return this.getFormQuestionRefByFormId(formId).where(
      "dashboardMetadata.patientInfoTag",
      "!=",
      null
    );
  }
  getVisitReasonFormQuestionRefsByFormId(formId: FormId) {
    return this.getFormQuestionRefByFormId(formId).where(
      "componentType",
      "==",
      FormQuestionComponents.VISIT_REASON_FIELD
    );
  }
  // getDependentQuestionsByClinicIdAndFormId(clinicId: ClinicId, formId: FormId): Query<DocumentData>{
  //   return this.formQuestionsRef
  //     .where("clinicId", "==", clinicId)
  //     .where("formId", "==", formId)
  //     .where('dependencies', '!=', null)
  // }
  getVaccineConfigRef(vaccineConfigId: string) {
    return this.vaccineConfigsRef.doc(requireId(vaccineConfigId, "vaccineConfigId"));
  }
  getVaccineConfigRefByClinicId(clinicId: ClinicId): Query<DocumentData> {
    return this.vaccineConfigsRef.where("clinicId", "==", clinicId);
  }
  getAppointmentConfigRef(appointmentConfigId: string) {
    return this.appointmentConfigsRef.doc(requireId(appointmentConfigId, "appointmentConfigId"));
  }
  getAppointmentConfigRefByClinicId(clinicId: ClinicId): Query<DocumentData> {
    return this.appointmentConfigsRef.where("clinicId", "==", clinicId);
  }
  getPostsRefByClinicId(clinicId: ClinicId): Query<DocumentData> {
    return this.postsRef.where("clinicId", "==", clinicId);
  }
  getScoringGroupRef(scoringGroupId: string) {
    return this.scoringGroupsRef.doc(requireId(scoringGroupId, "scoringGroupId"));
  }
  getScoringGroupsRefByClinicId(clinicId: ClinicId): Query<DocumentData> {
    return this.scoringGroupsRef.where("clinicId", "==", clinicId);
  }

  getPDFExportSettingFromFormId(formId: string): Query<DocumentData> {
    return this.pdfExportFormsRef.where("formId", "==", formId).limit(1);
  }
  getNotDeletedScoringGroupsRefByClinicId(
    clinicId: ClinicId
  ): Query<DocumentData> {
    return this.getScoringGroupsRefByClinicId(clinicId).where(
      "deleted",
      "==",
      false
    );
  }
  getPDFExportFormRef(formId: FormId): Query<DocumentData> {
    return this.pdfExportFormsRef.where("formId", "==", formId).limit(1);
  }
  getVaccineMenuSettingsRefByClinicId(clinicId: string): Query<DocumentData> {
    return this.vaccineMenuSettingsRef.where("clinicId", "==", clinicId)
  }
  getVaccineMenuSettingRef(menuId: string) {
    return this.vaccineMenuSettingsRef.doc(menuId)
  }
  getVaccineAppointmentDetailsRef(appointmentId: AppointmentId): DocumentReference<DocumentData> {
    return this.vaccineAppointmentDetailsRef.doc(requireId(appointmentId, "appointmentId"))
  }
  getSyncSignalRef(
    clinicId: ClinicId,
    collectionName: SyncSignalCollectionName,
    targetDocId?: string
  ): DocumentReference<DocumentData> {
    const docId = makeSyncSignalDocId(clinicId, collectionName, targetDocId);
    return this.syncSignalsRef.doc(requireId(docId, "syncSignalDocId"));
  }
  getTVConfigRef(clinicId: string): DocumentReference<DocumentData> {
    return this.tvConfigsRef.doc(clinicId);
  }
  getQueueConfigRef(clinicId: string): DocumentReference<DocumentData> {
    return this.queueConfigsRef.doc(clinicId);
  }
  getQueueEntryRef(entryId: string): DocumentReference<DocumentData> {
    return this.queueEntriesRef.doc(entryId);
  }
  getQueueEntriesRef(): CollectionReference<DocumentData> {
    return this.queueEntriesRef;
  }
  /**
   * Clinic queue listener query.
   * When checkInTimeSince is set, only recent entries are streamed (all statuses).
   */
  getQueueEntriesRefByClinicId(
    clinicId: string,
    checkInTimeSince?: Date
  ): Query<DocumentData> {
    let query: Query<DocumentData> = this.queueEntriesRef.where(
      "clinicId",
      "==",
      clinicId
    );
    if (checkInTimeSince) {
      query = query.where("checkInTime", ">=", checkInTimeSince);
    }
    return query;
  }
}
const fsLocations = new FsLocations();

export { FsLocations, fsLocations };

